/**
 * @file
 * A JavaScript file for the tab interface.
 *
 * Tab interface based on https://inclusive-components.design/tabbed-interfaces/#truetabbedinterfaces
 *
 * Heavily modified including:
 *   - Support for multiple tab groups on a page.
 *   - Convert static calls like `Array.prototype.indexOf` to method calls.
 *   - Replaced deprecated event 'which' property used to get the pressed key.
 *   - Renamed variables to be clearer.
 *   - Store collections of elements as arrays.
 *   - Re-order for readability.
 *
 * @copyright Copyright 2021 Palantir.net
 */

'use strict';

(function () {
  // Get relevant elements and collections
  const tabbedComponents = Array.from(document.querySelectorAll('.tabbed'));

  tabbedComponents.forEach((tabbedComponent) => {
    const tablist = tabbedComponent.querySelector('ul');
    const tabs = Array.from(tablist.querySelectorAll('a'));
    const panels = Array.from(tabbedComponent.querySelectorAll('[id^="section"]'));

    // Add the tablist role to the first <ul> in the .tabbed container
    tablist.setAttribute('role', 'tablist');

    // The tab switching function
    const switchTab = (oldTab, newTab) => {
      newTab.focus();
      // Make the active tab focusable by the user (Tab key)
      newTab.removeAttribute('tabindex');
      // Set the selected state
      newTab.setAttribute('aria-selected', 'true');
      oldTab.removeAttribute('aria-selected');
      oldTab.setAttribute('tabindex', '-1');

      // Get the indices of the new and old tabs to find the correct
      // tab panels to show and hide
      let newTabIndex = tabs.indexOf(newTab);
      let oldTabIndex = tabs.indexOf(oldTab);
      panels[oldTabIndex].hidden = true;
      panels[newTabIndex].hidden = false;
    };

    // Add semantics are remove user focusability for each tab
    tabs.forEach((tab, tabIndex) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('id', 'tab' + (tabIndex + 1));
      tab.setAttribute('tabindex', '-1');
      tab.parentNode.setAttribute('role', 'presentation');

      // Add tab panel semantics and hide them all
      panels.forEach( (panel, panelIndex) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('tabindex', '-1');
        panel.setAttribute('aria-labelledby', tabs[panelIndex].id);
        panel.hidden = true;
      });

      // Initially activate the first tab and reveal the first tab panel
      tabs[0].removeAttribute('tabindex');
      tabs[0].setAttribute('aria-selected', 'true');
      panels[0].hidden = false;

      // Handle clicking of tabs for mouse users
      tab.addEventListener('click', event => {
        event.preventDefault();
        let currentTab = tablist.querySelector('[aria-selected]');

        if (event.currentTarget !== currentTab) {
          switchTab(currentTab, event.currentTarget);
        }
      });

      // Handle keydown events for keyboard users
      tab.addEventListener('keydown', event => {
        let action = null;

        switch (event.key) {
          case 'ArrowLeft':
            action = 'left';
            break;

          case 'ArrowRight':
            action = 'right';
            break;

          case 'ArrowDown':
            action = 'down';
            break;
        }

        if (action !== null) {
          event.preventDefault();
        }

        switch (action) {
          case 'left':
            if (tabs[tabIndex - 1] !== undefined) {
              switchTab(event.currentTarget, tabs[tabIndex - 1]);
            }
            break;

          case 'right':
            if (tabs[tabIndex + 1] !== undefined) {
              switchTab(event.currentTarget, tabs[tabIndex + 1]);
            }
            break;

          case 'down':
            panels[tabIndex].focus();
            break;
        }
      });
    });
  });
})();
